import {useEffect} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {findNewValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {validateProcedure} from 'procedures/design/validation/validateProcedure';
import {trackEncounteredConfigurationValidationError} from 'procedures/procedureAnalyticsEventsUtil';
import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useArchivedLibraryParameterDefinitionIds, {
  getLibraryParameterDefinitionIdsFromMethods,
} from 'procedures/utils/useArchivedLibraryParameterDefinitionIds';
import useBioentitiesForProcedures, {
  getBioentityIdsFromMethods,
} from 'procedures/utils/useBioentitiesForProcedures';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

/**
 * Other than loading schemas, this hook has no side effects. Callers are require to cache
 * or otherwise respond to validation errors if needed.
 */
export default function useValidateProcedure(
  flowchart: ProcedureFlowchart | null,
  procedureType: ProcedureType,
  procedureId: string | null
): Array<ProcedureValidationError> {
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const bioentitiesForProcedures = useBioentitiesForProcedures(
    flowchart ? getBioentityIdsFromMethods(flowchart.nodes) : []
  );
  const archivedLibraryParameterDefinitionIds = useArchivedLibraryParameterDefinitionIds(
    flowchart ? getLibraryParameterDefinitionIdsFromMethods(flowchart.nodes) : []
  );

  const [procedureValidationErrors, setProcedureValidationErrors] = useStateIfMounted<
    Array<ProcedureValidationError>
  >(
    flowchart
      ? validateProcedure(flowchart, {
          tagSchemas,
          tagSchemasForProcedures,
          bioentitiesForProcedures,
          archivedLibraryParameterDefinitionIds,
          procedureType,
        })
      : []
  );

  useEffect(() => {
    const previousValidationErrors = procedureValidationErrors;

    const currentValidationErrors = flowchart
      ? validateProcedure(flowchart, {
          tagSchemas,
          tagSchemasForProcedures,
          bioentitiesForProcedures,
          archivedLibraryParameterDefinitionIds,
          procedureType,
        })
      : [];
    const newValidationErrors = findNewValidationErrors(previousValidationErrors, currentValidationErrors);
    if (procedureId) {
      trackEncounteredConfigurationValidationError(procedureId, procedureType, newValidationErrors);
    }
    setProcedureValidationErrors(currentValidationErrors);
    // Do not include procedureValidationErrors as a dependency to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchart, procedureType, tagSchemas]);

  return procedureValidationErrors;
}
